import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, IconButton, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Grid, Tooltip, CircularProgress, Alert, Card, CardContent,
  InputAdornment, useTheme, useMediaQuery, Divider, Tabs, Tab,
  Collapse, FormControlLabel, Switch,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Build as BuildIcon, Search as SearchIcon, Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon, Engineering as EngineeringIcon,
  Warning as WarningIcon, CheckCircle as CheckIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { maintenanceRecordService, machineService, sparePartService } from '../services';
import { useAuthStore } from '../store/authStore';

// ─── Types ───────────────────────────────────────────────────────────────────
interface MaintenanceRecord {
  id: number;
  record_date: string;
  machine_id: number;
  machine_name?: string;
  machine_code?: string;
  other_machine_name?: string;
  machine_area?: string;
  failure_status?: 'machine_running' | 'machine_stop' | string;
  severity?: 'low' | 'medium' | 'high' | string;
  maintenance_type?: 'breakdown' | 'preventive' | 'predictive' | 'machine_idle' | string;
  preventive_jobs?: string[];
  predictive_jobs?: string[];
  failure_start?: string;
  failure_end?: string;
  total_downtime_min?: number;
  failure_type?: string;
  failed_component?: string;
  failure_description?: string;
  maint_arrival?: string;
  repair_start?: string;
  repair_end?: string;
  actual_repair_time_min?: number;
  spare_required?: boolean | number;
  spare_specification?: string;
  spare_waiting_min?: number;
  technician_names?: string;
  root_cause?: string;
  corrective_action?: string;
  am_possible?: boolean | number | null;
  remarks?: string;
  created_by_name?: string;
  created_at?: string;
}

const FAILURE_TYPES = [
  'Mechanical', 'Electrical', 'Pneumatic', 'Hydraulic',
  'Software / PLC', 'Operator Error', 'Wear & Tear', 'Utility', 'Other',
];

const MAINTENANCE_TYPES = [
  { value: 'breakdown', label: 'Breakdown' },
  { value: 'preventive', label: 'Preventive' },
  { value: 'predictive', label: 'Predictive' },
  { value: 'machine_idle', label: 'Machine Idle' },
];

const FAILURE_STATUS_OPTIONS = [
  { value: 'machine_running', label: 'Machine Running' },
  { value: 'machine_stop', label: 'Machine Stop' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const emptyForm = (): Partial<MaintenanceRecord> => ({
  record_date: new Date().toISOString().split('T')[0],
  machine_id: 0,
  machine_area: '',
  failure_status: 'machine_stop',
  severity: 'medium',
  maintenance_type: 'breakdown',
  preventive_jobs: [],
  predictive_jobs: [],
  failure_start: '',
  failure_end: '',
  total_downtime_min: undefined,
  failure_type: '',
  failed_component: '',
  failure_description: '',
  maint_arrival: '',
  repair_start: '',
  repair_end: '',
  actual_repair_time_min: undefined,
  spare_required: false,
  spare_specification: '',
  spare_waiting_min: undefined,
  technician_names: '',
  root_cause: '',
  corrective_action: '',
  am_possible: null,
  remarks: '',
});

const dtLocal = (dt?: string) => {
  if (!dt) return '';
  const s = dt.replace(' ', 'T');
  return s.length === 16 ? s : s.slice(0, 16);
};

const fmtMin = (min?: number | null) => {
  if (min === null || min === undefined) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatCreatedAt = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatMaintenanceType = (value?: string) => {
  const match = MAINTENANCE_TYPES.find((option) => option.value === value);
  return match?.label || value || '—';
};

const formatFailureStatus = (value?: string) => {
  const match = FAILURE_STATUS_OPTIONS.find((option) => option.value === value);
  return match?.label || value || '—';
};

const formatSeverity = (value?: string) => {
  const match = SEVERITY_OPTIONS.find((option) => option.value === value);
  return match?.label || value || '—';
};

const getMaintenanceTypeColor = (value?: string) => {
  switch (value) {
    case 'preventive': return 'success';
    case 'predictive': return 'info';
    case 'machine_idle': return 'secondary';
    default: return 'warning';
  }
};

const getSeverityColor = (value?: string) => {
  switch (value) {
    case 'high': return 'error';
    case 'medium': return 'warning';
    case 'low': return 'success';
    default: return 'default';
  }
};

const getFailureStatusColor = (value?: string) => {
  switch (value) {
    case 'machine_stop': return 'error';
    case 'machine_running': return 'success';
    default: return 'default';
  }
};

// ─── Component ───────────────────────────────────────────────────────────────
interface MaintenanceManagementProps {
  maintenanceView?: 'preventive' | 'predictive';
}

const MaintenanceManagement: React.FC<MaintenanceManagementProps> = ({ maintenanceView }) => {
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'technician';
  const canDelete = user?.role === 'admin' || user?.role === 'manager';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Data state ──
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // ── Filters ──
  const [search, setSearch] = useState('');
  const [filterMachine, setFilterMachine] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMaintenanceType, setFilterMaintenanceType] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterFailureStatus, setFilterFailureStatus] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  // ── Dialog ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<MaintenanceRecord | null>(null);
  const [form, setForm] = useState<Partial<MaintenanceRecord>>(emptyForm());
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [otherMachineName, setOtherMachineName] = useState('');

  // ── Preventive / Predictive tab search ──
  const [pmSearch, setPmSearch] = useState('');
  const [pdSearch, setPdSearch] = useState('');

  // ── WhatsApp message dialog ──
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waMessage, setWaMessage] = useState('');
  const [waCopied, setWaCopied] = useState(false);

  // ── View record dialog ──
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<MaintenanceRecord | null>(null);

  // ── Form sections expand ──
  const [showSpare, setShowSpare] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [preventiveJobInput, setPreventiveJobInput] = useState('');
  const [predictiveJobInput, setPredictiveJobInput] = useState('');
  const [spareSearchInput, setSpareSearchInput] = useState('');
  const [spareSearchLoading, setSpareSearchLoading] = useState(false);
  const [spareOptions, setSpareOptions] = useState<any[]>([]);

  // ── Load ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterMachine) params.machine_id = filterMachine;
      if (filterType)    params.failure_type = filterType;
      if (filterMaintenanceType) params.maintenance_type = filterMaintenanceType;
      if (filterSeverity) params.severity = filterSeverity;
      if (filterFailureStatus) params.failure_status = filterFailureStatus;
      if (filterStart)   params.start_date = filterStart;
      if (filterEnd)     params.end_date = filterEnd;
      if (search)        params.search = search;

      const [recRes, machRes, statsRes] = await Promise.all([
        maintenanceRecordService.getAll(params),
        machineService.getAll(),
        maintenanceRecordService.getStats(params),
      ]);
      setRecords(recRes.records || []);
      setMachines(machRes.machines || []);
      setStats(statsRes);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filterMachine, filterType, filterMaintenanceType, filterSeverity, filterFailureStatus, filterStart, filterEnd, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const query = spareSearchInput.trim();
    if (!query || query.length < 2) {
      setSpareOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSpareSearchLoading(true);
        const res = await sparePartService.getAll({ search: query, status: 'active' });
        setSpareOptions(res.parts || []);
      } catch {
        setSpareOptions([]);
      } finally {
        setSpareSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [spareSearchInput]);

  // ── Dialog open ──
  const openDialog = (rec?: MaintenanceRecord) => {
    if (rec) {
      setSelectedRecord(rec);
      const isOther = !rec.machine_id && !!rec.other_machine_name;
      setOtherMachineName(isOther ? (rec.other_machine_name || '') : '');
      setForm({
        ...rec,
        machine_id: isOther ? -1 : rec.machine_id,
        failure_start: dtLocal(rec.failure_start),
        failure_end:   dtLocal(rec.failure_end),
        maint_arrival: dtLocal(rec.maint_arrival),
        repair_start:  dtLocal(rec.repair_start),
        repair_end:    dtLocal(rec.repair_end),
      });
      setShowSpare(!!rec.spare_required || !!rec.spare_specification);
      setShowAnalysis(!!rec.root_cause || !!rec.corrective_action);
      setPreventiveJobInput('');
      setPredictiveJobInput('');
      setSpareSearchInput('');
    } else {
      setSelectedRecord(null);
      setOtherMachineName('');
      setForm(emptyForm());
      setShowSpare(false);
      setShowAnalysis(false);
      setPreventiveJobInput('');
      setPredictiveJobInput('');
      setSpareSearchInput('');
    }
    setSaveError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setSelectedRecord(null); setOtherMachineName(''); };

  // Auto-calculate downtime when failure_start + failure_end set
  const handleFormChange = (field: string, value: any) => {
    const updated = { ...form, [field]: value };

    if (field === 'maintenance_type') {
      const mt = value as string;
      if (mt === 'preventive' || mt === 'predictive') {
        updated.failure_start = '';
        updated.failure_end = '';
        updated.total_downtime_min = undefined;
        updated.failure_type = '';
        updated.failed_component = '';
        updated.failure_description = '';
      }
      if (mt === 'preventive') {
        updated.predictive_jobs = [];
      }
      if (mt === 'predictive') {
        updated.preventive_jobs = [];
      }
    }

    if ((field === 'failure_start' || field === 'failure_end') &&
        updated.failure_start && updated.failure_end) {
      const diff = (new Date(updated.failure_end as string).getTime() -
                    new Date(updated.failure_start as string).getTime()) / 60000;
      if (diff > 0) updated.total_downtime_min = Math.round(diff);
    }
    if ((field === 'repair_start' || field === 'repair_end') &&
        updated.repair_start && updated.repair_end) {
      const diff = (new Date(updated.repair_end as string).getTime() -
                    new Date(updated.repair_start as string).getTime()) / 60000;
      if (diff > 0) updated.actual_repair_time_min = Math.round(diff);
    }
    setForm(updated);
  };

  const addPreventiveJob = () => {
    const value = preventiveJobInput.trim();
    if (!value) return;
    const existing = Array.isArray(form.preventive_jobs) ? form.preventive_jobs : [];
    if (!existing.includes(value)) {
      setForm({ ...form, preventive_jobs: [...existing, value] });
    }
    setPreventiveJobInput('');
  };

  const addPredictiveJob = () => {
    const value = predictiveJobInput.trim();
    if (!value) return;
    const existing = Array.isArray(form.predictive_jobs) ? form.predictive_jobs : [];
    if (!existing.includes(value)) {
      setForm({ ...form, predictive_jobs: [...existing, value] });
    }
    setPredictiveJobInput('');
  };

  const removePreventiveJob = (job: string) => {
    const existing = Array.isArray(form.preventive_jobs) ? form.preventive_jobs : [];
    setForm({ ...form, preventive_jobs: existing.filter((j) => j !== job) });
  };

  const removePredictiveJob = (job: string) => {
    const existing = Array.isArray(form.predictive_jobs) ? form.predictive_jobs : [];
    setForm({ ...form, predictive_jobs: existing.filter((j) => j !== job) });
  };

  // ── Save ──
  const handleSave = async () => {
    const isOther = form.machine_id === -1;
    if (!form.record_date || (!form.machine_id && !isOther)) {
      setSaveError('Date and Machine are required');
      return;
    }
    if (isOther && !otherMachineName.trim()) {
      setSaveError('Please enter the machine name');
      return;
    }
    if (!form.failure_status) {
      setSaveError('Failure status is required');
      return;
    }
    if (!form.severity) {
      setSaveError('Severity is required');
      return;
    }
    if (!form.maintenance_type) {
      setSaveError('Maintenance type is required');
      return;
    }
    if (form.maintenance_type === 'preventive' && (!Array.isArray(form.preventive_jobs) || form.preventive_jobs.length === 0)) {
      setSaveError('Add at least one preventive job');
      return;
    }
    if (form.maintenance_type === 'predictive' && (!Array.isArray(form.predictive_jobs) || form.predictive_jobs.length === 0)) {
      setSaveError('Add at least one predictive job');
      return;
    }
    try {
      setSaveLoading(true);
      setSaveError(null);
      const payload = {
        ...form,
        machine_id: isOther ? null : form.machine_id,
        other_machine_name: isOther ? otherMachineName.trim() : null,
      };
      if (selectedRecord) {
        await maintenanceRecordService.update(selectedRecord.id, payload);
      } else {
        await maintenanceRecordService.create(payload);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      closeDialog();
      fetchData();
    } catch (e: any) {
      setSaveError(e.response?.data?.error || 'Failed to save record');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this maintenance record?')) return;
    try {
      await maintenanceRecordService.delete(id);
      fetchData();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to delete');
    }
  };

  // ── Preventive / Predictive dedicated views (derived from loaded records) ──
  const matchesJobSearch = (r: MaintenanceRecord, jobs: string[] | undefined, term: string) => {
    const q = term.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      r.machine_name, r.machine_code, r.machine_area, r.technician_names,
      r.remarks, r.root_cause, r.corrective_action,
      ...(Array.isArray(jobs) ? jobs : []),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  };

  const preventiveRecords = records
    .filter((r) => r.maintenance_type === 'preventive')
    .filter((r) => matchesJobSearch(r, r.preventive_jobs, pmSearch));

  const predictiveRecords = records
    .filter((r) => r.maintenance_type === 'predictive')
    .filter((r) => matchesJobSearch(r, r.predictive_jobs, pdSearch));

  const renderMaintenanceJobCards = (jobRecords: MaintenanceRecord[], jobType: 'preventive' | 'predictive') => {
    const jobsKey = jobType === 'preventive' ? 'preventive_jobs' : 'predictive_jobs';
    const color = jobType === 'preventive' ? 'success' : 'info';
    const emptyMessage = `No ${jobType} maintenance records found`;

    if (jobRecords.length === 0) {
      return <Paper sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">{emptyMessage}</Typography></Paper>;
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {jobRecords.map((record) => {
          const jobs = record[jobsKey] || [];
          return (
            <Card key={record.id} onClick={() => { setViewRecord(record); setViewOpen(true); }} sx={{ cursor: 'pointer', borderLeft: `4px solid ${jobType === 'preventive' ? '#2e7d32' : '#0288d1'}` }}>
              <CardContent sx={{ p: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Box>
                    <Typography fontWeight="bold">{record.machine_name || record.other_machine_name || '—'}</Typography>
                    <Typography variant="caption" color="textSecondary">{record.record_date} · {record.machine_area || 'No area'}</Typography>
                  </Box>
                  <Chip label={formatSeverity(record.severity)} size="small" color={getSeverityColor(record.severity)} variant="outlined" />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {jobs.map((job, index) => <Chip key={index} label={job} size="small" color={color} variant="outlined" />)}
                </Box>
                <Typography variant="body2" color="textSecondary">Technicians: {record.technician_names || '—'}</Typography>
                <Typography variant="caption" color="textSecondary">Recorded by {record.created_by_name || '—'} · Added {formatCreatedAt(record.created_at)}</Typography>
                {record.remarks && <Typography variant="body2" sx={{ mt: 0.5 }}>Remarks: {record.remarks}</Typography>}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }} onClick={(event) => event.stopPropagation()}>
                  {canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(record)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                  {canDelete && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(record.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  // ── WhatsApp shareable message for the selected date range ──
  const generateWhatsAppMessage = () => {
    if (!filterStart || !filterEnd) {
      alert('Please select both a From Date and To Date first, then generate the message.');
      return;
    }

    if (records.length === 0) {
      alert('No maintenance jobs found for the selected date range.');
      return;
    }

    const byMachine = new Map<string, MaintenanceRecord[]>();
    records.forEach((r) => {
      const key = r.machine_name || r.other_machine_name || 'Unknown Machine';
      if (!byMachine.has(key)) byMachine.set(key, []);
      byMachine.get(key)!.push(r);
    });

    const lines: string[] = [];
    lines.push(`🔧 *Maintenance Report*`);
    lines.push('');

    byMachine.forEach((machineRecords, machineName) => {
      lines.push(`*${machineName}*`);
      machineRecords.forEach((r) => {
        lines.push(`  • ${formatMaintenanceType(r.maintenance_type)}`);
        if (r.failure_type) lines.push(`    Failure Type: ${r.failure_type}`);
        if (r.failed_component) lines.push(`    Failed Component: ${r.failed_component}`);
        if (r.failure_description) lines.push(`    Failure Description: ${r.failure_description}`);
        if (Array.isArray(r.preventive_jobs) && r.preventive_jobs.length > 0) lines.push(`    Preventive Jobs: ${r.preventive_jobs.join(', ')}`);
        if (Array.isArray(r.predictive_jobs) && r.predictive_jobs.length > 0) lines.push(`    Predictive Jobs: ${r.predictive_jobs.join(', ')}`);
        if (r.remarks) lines.push(`    Remarks: ${r.remarks}`);
      });
      lines.push('');
    });

    setWaMessage(lines.join('\n'));
    setWaCopied(false);
    setWaDialogOpen(true);
  };

  const handleCopyWaMessage = async () => {
    try {
      await navigator.clipboard.writeText(waMessage);
      setWaCopied(true);
      setTimeout(() => setWaCopied(false), 2000);
    } catch {
      alert('Failed to copy. Please select the text manually and copy.');
    }
  };

  // ── CSV export ──
  const handleExportCSV = () => {
    const headers = [
      'Date','Machine','Other Machine','Area','Failure Status','Severity','Maintenance Type',
      'Failure Start','Failure End','Total Downtime (min)',
      'Failure Type','Failed Component','Failure Description',
      'Maint. Arrival','Repair Start','Repair End','Repair Time (min)',
      'Spare Required','Spare Spec','Spare Waiting (min)',
      'Technicians','Root Cause','Corrective Action','AM Possible','Remarks',
      'Preventive Jobs','Predictive Jobs','Created By',
    ];
    const rows = records.map(r => {
      // Format preventive jobs
      let preventiveJobsStr = '';
      if (r.preventive_jobs && Array.isArray(r.preventive_jobs) && r.preventive_jobs.length > 0) {
        preventiveJobsStr = r.preventive_jobs.join('; ');
      }
      
      // Format predictive jobs
      let predictiveJobsStr = '';
      if (r.predictive_jobs && Array.isArray(r.predictive_jobs) && r.predictive_jobs.length > 0) {
        predictiveJobsStr = r.predictive_jobs.join('; ');
      }
      
      return [
        r.record_date, 
        r.machine_name, 
        r.other_machine_name || '', 
        r.machine_area,
        r.failure_status || '',
        r.severity || '',
        r.maintenance_type || '',
        r.failure_start, 
        r.failure_end, 
        r.total_downtime_min,
        r.failure_type, 
        r.failed_component, 
        r.failure_description,
        r.maint_arrival, 
        r.repair_start, 
        r.repair_end, 
        r.actual_repair_time_min,
        r.spare_required ? 'Y' : 'N', 
        r.spare_specification, 
        r.spare_waiting_min,
        r.technician_names, 
        r.root_cause, 
        r.corrective_action,
        r.am_possible === null || r.am_possible === undefined ? '' : (r.am_possible ? 'Y' : 'N'),
        r.remarks,
        preventiveJobsStr,
        predictiveJobsStr,
        r.created_by_name || '',
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`);
    });
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `maintenance_records_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon sx={{ color: '#00695C', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Maintenance Management System</Typography>
            <Typography variant="body2" color="textSecondary">Track machine failures, downtime, and corrective actions</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={fetchData}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExportCSV}>CSV</Button>
          <Button variant="outlined" color="success" size="small" startIcon={<WhatsAppIcon />} onClick={generateWhatsAppMessage}>
            WhatsApp Message
          </Button>
          {canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
              sx={{ bgcolor: '#00695C' }}>
              New Record
            </Button>
          )}
        </Box>
      </Box>

      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Saved successfully</Alert>}

      {/* Stats Cards */}
      {stats?.summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total Records', value: stats.summary.total_records, color: '#00695C', icon: <BuildIcon /> },
            { label: 'Total Downtime', value: fmtMin(stats.summary.total_downtime_min), color: '#d32f2f', icon: <TimeIcon /> },
            { label: 'Avg Downtime', value: fmtMin(Math.round(stats.summary.avg_downtime_min)), color: '#f57c00', icon: <WarningIcon /> },
            { label: 'Machines Affected', value: stats.summary.machines_affected, color: '#1565c0', icon: <EngineeringIcon /> },
            { label: 'Spares Required', value: stats.summary.spare_required_count, color: '#6a1b9a', icon: <CheckIcon /> },
          ].map((s) => (
            <Grid item xs={6} sm={4} md={2.4} key={s.label}>
              <Card sx={{ borderLeft: `4px solid ${s.color}` }}>
                <CardContent sx={{ p: '12px !important' }}>
                  <Typography variant="caption" color="textSecondary">{s.label}</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: s.color }}>{s.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper sx={{ width: '100%' }}>
        {!maintenanceView && (
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }} variant="scrollable" scrollButtons="auto">
            <Tab label="Records" />
            <Tab label="By Failure Type" />
            <Tab label="By Machine" />
          </Tabs>
        )}

        <Box sx={{ p: 2 }}>
          {/* Filters */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" placeholder="Search..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size="small" label="Machine" value={filterMachine}
                onChange={(e) => setFilterMachine(e.target.value)}>
                <MenuItem value="">All Machines</MenuItem>
                {machines.map(m => <MenuItem key={m.id} value={m.id}>{m.machine_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size="small" label="Failure Type" value={filterType}
                onChange={(e) => setFilterType(e.target.value)}>
                <MenuItem value="">All Types</MenuItem>
                {FAILURE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size="small" label="Maintenance Type" value={filterMaintenanceType}
                onChange={(e) => setFilterMaintenanceType(e.target.value)}>
                <MenuItem value="">All Types</MenuItem>
                {MAINTENANCE_TYPES.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size="small" label="Severity" value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}>
                <MenuItem value="">All Severities</MenuItem>
                {SEVERITY_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size="small" label="Failure Status" value={filterFailureStatus}
                onChange={(e) => setFilterFailureStatus(e.target.value)}>
                <MenuItem value="">All Statuses</MenuItem>
                {FAILURE_STATUS_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size="small" label="From Date" type="date" value={filterStart}
                onChange={(e) => setFilterStart(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size="small" label="To Date" type="date" value={filterEnd}
                onChange={(e) => setFilterEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={3} md={1}>
              <Button fullWidth variant="outlined" size="small" sx={{ height: 40 }}
                onClick={() => { setSearch(''); setFilterMachine(''); setFilterType(''); setFilterMaintenanceType(''); setFilterSeverity(''); setFilterFailureStatus(''); setFilterStart(''); setFilterEnd(''); }}>
                Clear
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : !maintenanceView && tabValue === 0 ? (
            /* ── Records Table ── */
            !isMobile ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Machine</strong></TableCell>
                      <TableCell><strong>Area</strong></TableCell>
                      <TableCell><strong>Status / Type</strong></TableCell>
                      <TableCell><strong>Failure Type</strong></TableCell>
                      <TableCell><strong>Failed Component</strong></TableCell>
                      <TableCell align="right"><strong>Downtime</strong></TableCell>
                      <TableCell align="right"><strong>Repair Time</strong></TableCell>
                      <TableCell align="center"><strong>Spare</strong></TableCell>
                      <TableCell><strong>Technicians</strong></TableCell>
                      <TableCell><strong>Recorded By / Added</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">No maintenance records found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : records.map((r) => (
                      <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => { setViewRecord(r); setViewOpen(true); }}>
                        <TableCell>{r.record_date}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{r.machine_name}</Typography>
                          <Typography variant="caption" color="textSecondary">{r.machine_code}</Typography>
                        </TableCell>
                        <TableCell>{r.machine_area || '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            <Chip label={formatMaintenanceType(r.maintenance_type)} size="small" color={getMaintenanceTypeColor(r.maintenance_type)} variant="outlined" />
                            <Chip label={formatSeverity(r.severity)} size="small" color={getSeverityColor(r.severity)} variant="outlined" />
                            <Chip label={formatFailureStatus(r.failure_status)} size="small" color={getFailureStatusColor(r.failure_status)} variant="outlined" />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {r.failure_type ? (
                            <Chip label={r.failure_type} size="small" color="warning" variant="outlined" />
                          ) : '—'}
                        </TableCell>
                        <TableCell>{r.failed_component || '—'}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={r.total_downtime_min && r.total_downtime_min > 120 ? 'error' : 'textPrimary'} fontWeight="bold">
                            {fmtMin(r.total_downtime_min)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtMin(r.actual_repair_time_min)}</TableCell>
                        <TableCell align="center">
                          <Chip label={r.spare_required ? 'Y' : 'N'} size="small"
                            color={r.spare_required ? 'error' : 'success'} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{r.technician_names || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{r.created_by_name || '—'}</Typography>
                          <Typography variant="caption" color="textSecondary">Added {formatCreatedAt(r.created_at)}</Typography>
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {canEdit && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openDialog(r)}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(r.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              /* ── Mobile Cards ── */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {records.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">No records found</Typography></Paper>
                ) : records.map((r) => (
                  <Card key={r.id} sx={{ borderLeft: '4px solid #f57c00' }}
                    onClick={() => { setViewRecord(r); setViewOpen(true); }}>
                    <CardContent sx={{ p: '12px !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                          <Typography fontWeight="bold">{r.machine_name}</Typography>
                          <Typography variant="caption" color="textSecondary">{r.record_date} · {r.machine_area}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          {r.failure_type && <Chip label={r.failure_type} size="small" color="warning" variant="outlined" />}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                        <Chip label={formatMaintenanceType(r.maintenance_type)} size="small" color={getMaintenanceTypeColor(r.maintenance_type)} variant="outlined" />
                        <Chip label={formatSeverity(r.severity)} size="small" color={getSeverityColor(r.severity)} variant="outlined" />
                        <Chip label={formatFailureStatus(r.failure_status)} size="small" color={getFailureStatusColor(r.failure_status)} variant="outlined" />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        {r.failed_component && <><strong>Component:</strong> {r.failed_component} · </>}
                        <strong>Downtime:</strong> {fmtMin(r.total_downtime_min)}
                      </Typography>
                      {r.technician_names && <Typography variant="caption" color="textSecondary">👤 {r.technician_names}</Typography>}
                      <Typography variant="caption" display="block" color="textSecondary">Recorded by {r.created_by_name || '—'} · Added {formatCreatedAt(r.created_at)}</Typography>
                      {canEdit && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}
                          onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" onClick={() => openDialog(r)}><EditIcon fontSize="small" /></IconButton>
                          {canDelete && <IconButton size="small" onClick={() => handleDelete(r.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )
          ) : !maintenanceView && tabValue === 1 ? (
            /* ── By Failure Type ── */
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Failure Type</strong></TableCell>
                    <TableCell align="right"><strong>Incidents</strong></TableCell>
                    <TableCell align="right"><strong>Total Downtime</strong></TableCell>
                    <TableCell align="right"><strong>Avg Downtime</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.byType || []).map((t: any) => (
                    <TableRow key={t.failure_type} hover>
                      <TableCell><Chip label={t.failure_type} color="warning" variant="outlined" size="small" /></TableCell>
                      <TableCell align="right"><strong>{t.count}</strong></TableCell>
                      <TableCell align="right">{fmtMin(t.total_downtime)}</TableCell>
                      <TableCell align="right">{fmtMin(t.count ? Math.round(t.total_downtime / t.count) : 0)}</TableCell>
                    </TableRow>
                  ))}
                  {(!stats?.byType || stats.byType.length === 0) && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><Typography color="textSecondary">No data</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : !maintenanceView && tabValue === 2 ? (
            /* ── By Machine ── */
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Machine</strong></TableCell>
                    <TableCell><strong>Code</strong></TableCell>
                    <TableCell align="right"><strong>Incidents</strong></TableCell>
                    <TableCell align="right"><strong>Total Downtime</strong></TableCell>
                    <TableCell align="right"><strong>Avg Downtime</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.byMachine || []).map((m: any) => (
                    <TableRow key={m.machine_code} hover>
                      <TableCell><Typography fontWeight="bold">{m.machine_name}</Typography></TableCell>
                      <TableCell>{m.machine_code}</TableCell>
                      <TableCell align="right"><strong>{m.count}</strong></TableCell>
                      <TableCell align="right">{fmtMin(m.total_downtime)}</TableCell>
                      <TableCell align="right">{fmtMin(m.count ? Math.round(m.total_downtime / m.count) : 0)}</TableCell>
                    </TableRow>
                  ))}
                  {(!stats?.byMachine || stats.byMachine.length === 0) && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><Typography color="textSecondary">No data</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : maintenanceView === 'preventive' ? (
            /* ── Preventive Maintenance ── */
            <Box>
              <TextField
                fullWidth size="small" placeholder="Search preventive jobs, machine, technician, remarks..."
                value={pmSearch} onChange={(e) => setPmSearch(e.target.value)}
                sx={{ mb: 2, maxWidth: 420 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              {isMobile ? renderMaintenanceJobCards(preventiveRecords, 'preventive') : <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Machine</strong></TableCell>
                      <TableCell><strong>Area</strong></TableCell>
                      <TableCell><strong>Severity</strong></TableCell>
                      <TableCell><strong>Preventive Jobs</strong></TableCell>
                      <TableCell><strong>Technicians</strong></TableCell>
                      <TableCell><strong>Remarks</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preventiveRecords.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="textSecondary">No preventive maintenance records found</Typography></TableCell></TableRow>
                    ) : preventiveRecords.map((r) => (
                      <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => { setViewRecord(r); setViewOpen(true); }}>
                        <TableCell>{r.record_date}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{r.machine_name}</Typography>
                          <Typography variant="caption" color="textSecondary">{r.machine_code}</Typography>
                        </TableCell>
                        <TableCell>{r.machine_area || '—'}</TableCell>
                        <TableCell><Chip label={formatSeverity(r.severity)} size="small" color={getSeverityColor(r.severity)} variant="outlined" /></TableCell>
                        <TableCell>
                          {Array.isArray(r.preventive_jobs) && r.preventive_jobs.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 260 }}>
                              {r.preventive_jobs.map((job, i) => <Chip key={i} label={job} size="small" color="success" variant="outlined" />)}
                            </Box>
                          ) : '—'}
                        </TableCell>
                        <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{r.technician_names || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{r.remarks || '—'}</Typography></TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                          {canDelete && <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(r.id)} color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>}
            </Box>
          ) : (
            /* ── Predictive Maintenance ── */
            <Box>
              <TextField
                fullWidth size="small" placeholder="Search predictive jobs, machine, technician, remarks..."
                value={pdSearch} onChange={(e) => setPdSearch(e.target.value)}
                sx={{ mb: 2, maxWidth: 420 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              {isMobile ? renderMaintenanceJobCards(predictiveRecords, 'predictive') : <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Machine</strong></TableCell>
                      <TableCell><strong>Area</strong></TableCell>
                      <TableCell><strong>Severity</strong></TableCell>
                      <TableCell><strong>Predictive Jobs</strong></TableCell>
                      <TableCell><strong>Technicians</strong></TableCell>
                      <TableCell><strong>Remarks</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {predictiveRecords.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="textSecondary">No predictive maintenance records found</Typography></TableCell></TableRow>
                    ) : predictiveRecords.map((r) => (
                      <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => { setViewRecord(r); setViewOpen(true); }}>
                        <TableCell>{r.record_date}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{r.machine_name}</Typography>
                          <Typography variant="caption" color="textSecondary">{r.machine_code}</Typography>
                        </TableCell>
                        <TableCell>{r.machine_area || '—'}</TableCell>
                        <TableCell><Chip label={formatSeverity(r.severity)} size="small" color={getSeverityColor(r.severity)} variant="outlined" /></TableCell>
                        <TableCell>
                          {Array.isArray(r.predictive_jobs) && r.predictive_jobs.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 260 }}>
                              {r.predictive_jobs.map((job, i) => <Chip key={i} label={job} size="small" color="info" variant="outlined" />)}
                            </Box>
                          ) : '—'}
                        </TableCell>
                        <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{r.technician_names || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{r.remarks || '—'}</Typography></TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {canEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                          {canDelete && <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(r.id)} color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>}
            </Box>
          )}
        </Box>
      </Paper>

      {/* ─── Add/Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ bgcolor: '#00695C', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon />
            {selectedRecord ? 'Edit Maintenance Record' : 'New Maintenance Record'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>

            {/* ── Section 1: Basic Info ── */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>📋 Basic Information</Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Date *" type="date" size="small" value={form.record_date || ''}
                onChange={(e) => handleFormChange('record_date', e.target.value)}
                InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Machine *" size="small" value={form.machine_id || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  handleFormChange('machine_id', val);
                  if (Number(val) !== -1) setOtherMachineName('');
                }} required>
                <MenuItem value="">Select Machine</MenuItem>
                {machines.map(m => <MenuItem key={m.id} value={m.id}>{m.machine_name} ({m.machine_code})</MenuItem>)}
                <MenuItem value={-1}>Other</MenuItem>
              </TextField>
              {form.machine_id === -1 && (
                <TextField fullWidth label="Machine Name *" size="small" value={otherMachineName}
                  onChange={(e) => setOtherMachineName(e.target.value)}
                  placeholder="Enter machine name" sx={{ mt: 1 }} />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Specific Area of Machine" size="small" value={form.machine_area || ''}
                onChange={(e) => handleFormChange('machine_area', e.target.value)} placeholder="e.g. Printing Head Unit" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Failure Status *" size="small" value={form.failure_status || ''}
                onChange={(e) => handleFormChange('failure_status', e.target.value)} required>
                {FAILURE_STATUS_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Severity *" size="small" value={form.severity || ''}
                onChange={(e) => handleFormChange('severity', e.target.value)} required>
                {SEVERITY_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Maintenance Type *" size="small" value={form.maintenance_type || ''}
                onChange={(e) => handleFormChange('maintenance_type', e.target.value)} required>
                {MAINTENANCE_TYPES.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </TextField>
            </Grid>

            {/* ── Section: Preventive / Predictive Jobs ── */}
            {form.maintenance_type === 'preventive' && (
              <>
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>✅ Preventive Job List</Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} sm={9}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Add Preventive Job"
                    value={preventiveJobInput}
                    onChange={(e) => setPreventiveJobInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPreventiveJob();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button fullWidth variant="outlined" onClick={addPreventiveJob}>Add Job</Button>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(form.preventive_jobs || []).map((job) => (
                      <Chip key={job} label={job} onDelete={() => removePreventiveJob(job)} color="success" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              </>
            )}

            {form.maintenance_type === 'predictive' && (
              <>
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#1565c0', fontWeight: 'bold', mb: 1 }}>📈 Predictive Job List</Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} sm={9}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Add Predictive Job"
                    value={predictiveJobInput}
                    onChange={(e) => setPredictiveJobInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPredictiveJob();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button fullWidth variant="outlined" onClick={addPredictiveJob}>Add Job</Button>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(form.predictive_jobs || []).map((job) => (
                      <Chip key={job} label={job} onDelete={() => removePredictiveJob(job)} color="info" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              </>
            )}

            {/* ── Section 2: Failure Timeline ── */}
            {form.maintenance_type !== 'preventive' && form.maintenance_type !== 'predictive' && (
              <>
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="error" fontWeight="bold" sx={{ mb: 1 }}>⚠️ Failure Timeline</Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Failure Start" type="datetime-local" size="small"
                    value={form.failure_start || ''} onChange={(e) => handleFormChange('failure_start', e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Failure End" type="datetime-local" size="small"
                    value={form.failure_end || ''} onChange={(e) => handleFormChange('failure_end', e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Total Downtime (min)" type="number" size="small"
                    value={form.total_downtime_min ?? ''} onChange={(e) => handleFormChange('total_downtime_min', e.target.value ? parseInt(e.target.value) : undefined)}
                    helperText="Auto-calculated from start/end" />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField select fullWidth label="Failure Type" size="small"
                    value={form.failure_type || ''} onChange={(e) => handleFormChange('failure_type', e.target.value)}>
                    <MenuItem value="">Select Type</MenuItem>
                    {FAILURE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Failed Component" size="small" value={form.failed_component || ''}
                    onChange={(e) => handleFormChange('failed_component', e.target.value)} placeholder="e.g. Servo Motor, Drive Belt" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Failure Description" size="small" multiline rows={2}
                    value={form.failure_description || ''} onChange={(e) => handleFormChange('failure_description', e.target.value)} />
                </Grid>
              </>
            )}

            {/* ── Section 3: Maintenance Timeline ── */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#1565c0', fontWeight: 'bold', mb: 1 }}>🔧 Maintenance Timeline</Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Maint. Arrival" type="datetime-local" size="small"
                value={form.maint_arrival || ''} onChange={(e) => handleFormChange('maint_arrival', e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Repair Start" type="datetime-local" size="small"
                value={form.repair_start || ''} onChange={(e) => handleFormChange('repair_start', e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Repair End" type="datetime-local" size="small"
                value={form.repair_end || ''} onChange={(e) => handleFormChange('repair_end', e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Actual Repair Time (min)" type="number" size="small"
                value={form.actual_repair_time_min ?? ''} onChange={(e) => handleFormChange('actual_repair_time_min', e.target.value ? parseInt(e.target.value) : undefined)}
                helperText="Auto-calculated from start/end" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Name of Technicians" size="small" value={form.technician_names || ''}
                onChange={(e) => handleFormChange('technician_names', e.target.value)}
                placeholder="e.g. Kamal Perera, Nimal Silva" />
            </Grid>

            {/* ── Section 4: Spare Parts (collapsible) ── */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                onClick={() => setShowSpare(!showSpare)}>
                <Typography variant="subtitle2" sx={{ color: '#6a1b9a', fontWeight: 'bold', flexGrow: 1 }}>🔩 Spare Parts</Typography>
                {showSpare ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Collapse in={showSpare}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControlLabel
                      control={<Switch checked={!!form.spare_required} onChange={(e) => handleFormChange('spare_required', e.target.checked)} />}
                      label="Spare Required" />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Autocomplete
                      freeSolo
                      options={spareOptions}
                      loading={spareSearchLoading}
                      getOptionLabel={(option: any) => {
                        if (typeof option === 'string') return option;
                        return `${option.part_number || ''} - ${option.part_name || ''}`.trim();
                      }}
                      onInputChange={(_, value) => {
                        setSpareSearchInput(value);
                        handleFormChange('spare_specification', value);
                      }}
                      onChange={(_, value: any) => {
                        if (!value) return;
                        if (typeof value === 'string') {
                          handleFormChange('spare_specification', value);
                        } else {
                          handleFormChange('spare_specification', `${value.part_number || ''} - ${value.part_name || ''}`.trim());
                        }
                      }}
                      inputValue={form.spare_specification || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Spare Specification"
                          size="small"
                          placeholder="Search spare parts by code or name"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Spare Waiting Time (min)" type="number" size="small"
                      value={form.spare_waiting_min ?? ''} onChange={(e) => handleFormChange('spare_waiting_min', e.target.value ? parseInt(e.target.value) : undefined)} />
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>

            {/* ── Section 5: Analysis (collapsible) ── */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                onClick={() => setShowAnalysis(!showAnalysis)}>
                <Typography variant="subtitle2" sx={{ color: '#00695C', fontWeight: 'bold', flexGrow: 1 }}>🔍 Root Cause & Corrective Action</Typography>
                {showAnalysis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Collapse in={showAnalysis}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Root Cause" size="small" multiline rows={3}
                      value={form.root_cause || ''} onChange={(e) => handleFormChange('root_cause', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Corrective Action" size="small" multiline rows={3}
                      value={form.corrective_action || ''} onChange={(e) => handleFormChange('corrective_action', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select fullWidth label="AM Possible (Autonomous Maint.)" size="small"
                      value={form.am_possible === null || form.am_possible === undefined ? '' : (form.am_possible ? 'Y' : 'N')}
                      onChange={(e) => handleFormChange('am_possible', e.target.value === '' ? null : e.target.value === 'Y')}>
                      <MenuItem value="">N/A</MenuItem>
                      <MenuItem value="Y">Yes</MenuItem>
                      <MenuItem value="N">No</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth label="Remarks" size="small" multiline rows={2}
                      value={form.remarks || ''} onChange={(e) => handleFormChange('remarks', e.target.value)} />
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : undefined}
            sx={{ bgcolor: '#00695C' }}>
            {selectedRecord ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── View Record Dialog ───────────────────────────────────────────── */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        {viewRecord && (
          <>
            <DialogTitle sx={{ bgcolor: '#00695C', color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildIcon />
                  Maintenance Record #{viewRecord.id}
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>{viewRecord.record_date}</Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {[
                {
                  title: '📋 Basic Information',
                  rows: [
                    ['Machine', `${viewRecord.machine_name} (${viewRecord.machine_code})`],
                    ['Area', viewRecord.machine_area],
                    ['Failure Status', viewRecord.failure_status === 'machine_running' ? 'Machine Running' : viewRecord.failure_status === 'machine_stop' ? 'Machine Stop' : viewRecord.failure_status],
                    ['Severity', viewRecord.severity ? String(viewRecord.severity).toUpperCase() : ''],
                    ['Maintenance Type', viewRecord.maintenance_type ? String(viewRecord.maintenance_type).replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : ''],
                    ['Recorded By', viewRecord.created_by_name],
                    ['Added to System', formatCreatedAt(viewRecord.created_at)],
                  ],
                },
                {
                  title: '✅ Preventive Jobs',
                  rows: Array.isArray(viewRecord.preventive_jobs) && viewRecord.preventive_jobs.length > 0
                    ? viewRecord.preventive_jobs.map((job, i) => [`Job ${i + 1}`, job])
                    : [],
                },
                {
                  title: '📈 Predictive Jobs',
                  rows: Array.isArray(viewRecord.predictive_jobs) && viewRecord.predictive_jobs.length > 0
                    ? viewRecord.predictive_jobs.map((job, i) => [`Job ${i + 1}`, job])
                    : [],
                },
                {
                  title: '⚠️ Failure Details',
                  rows: [
                    ['Failure Start', viewRecord.failure_start],
                    ['Failure End', viewRecord.failure_end],
                    ['Total Downtime', fmtMin(viewRecord.total_downtime_min)],
                    ['Failure Type', viewRecord.failure_type],
                    ['Failed Component', viewRecord.failed_component],
                    ['Description', viewRecord.failure_description],
                  ],
                },
                {
                  title: '🔧 Maintenance',
                  rows: [
                    ['Maint. Arrival', viewRecord.maint_arrival],
                    ['Repair Start', viewRecord.repair_start],
                    ['Repair End', viewRecord.repair_end],
                    ['Actual Repair Time', fmtMin(viewRecord.actual_repair_time_min)],
                    ['Technicians', viewRecord.technician_names],
                  ],
                },
                {
                  title: '🔩 Spare Parts',
                  rows: [
                    ['Spare Required', viewRecord.spare_required ? 'Yes' : 'No'],
                    ['Specification', viewRecord.spare_specification],
                    ['Spare Waiting', fmtMin(viewRecord.spare_waiting_min)],
                  ],
                },
                {
                  title: '🔍 Analysis',
                  rows: [
                    ['Root Cause', viewRecord.root_cause],
                    ['Corrective Action', viewRecord.corrective_action],
                    ['AM Possible', viewRecord.am_possible === null || viewRecord.am_possible === undefined ? 'N/A' : (viewRecord.am_possible ? 'Yes' : 'No')],
                    ['Remarks', viewRecord.remarks],
                  ],
                },
              ].filter((section) => {
                // Hide Failure Details for preventive and predictive maintenance
                if (section.title === '⚠️ Failure Details') {
                  return viewRecord.maintenance_type !== 'preventive' && viewRecord.maintenance_type !== 'predictive';
                }
                // Hide preventive jobs section if no jobs exist
                if (section.title === '✅ Preventive Jobs' && section.rows.length === 0) {
                  return false;
                }
                // Hide predictive jobs section if no jobs exist
                if (section.title === '📈 Predictive Jobs' && section.rows.length === 0) {
                  return false;
                }
                return true;
              }).map((section) => (
                <Box key={section.title} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>{section.title}</Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Grid container spacing={1}>
                    {section.rows.filter(([, v]) => v).map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Typography variant="caption" color="textSecondary">{label}</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value as string}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              {canEdit && <Button variant="outlined" onClick={() => { setViewOpen(false); openDialog(viewRecord); }}>Edit</Button>}
              <Button onClick={() => setViewOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ─── WhatsApp Message Dialog ─────────────────────────────────────── */}
      <Dialog open={waDialogOpen} onClose={() => setWaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#25D366', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon /> WhatsApp Message Preview
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Copy this message and paste it into WhatsApp.
          </Typography>
          <TextField
            fullWidth multiline rows={16} value={waMessage}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            onFocus={(e) => e.target.select()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWaDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyWaMessage}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ea952' } }}
          >
            {waCopied ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceManagement;
